import { useContext, useState, useMemo } from 'react';
import { UnitsContext } from '../../../../../../Contexts/UnitsContext';
import { Modalv2Context } from '../../../../../../Contexts/Modalv2Context';
import {
	AB_SPLIT_TEST_CONSTANTS,
	EXPB_DOC_CONSTANTS,
	SPLIT_CONFIG_TYPES,
	CONFIG_KEYDATA_CONSTANTS,
	MULTI_SPLIT_TEST_CONSTANTS,
	SPLIT_LEVELS
} from '../../../../../../core/constants';
import useCommonStyles from '../../../../../../commonStyles';
import { getSectionNameToDisplay } from '../helpers';

// MUI Imports
import { Box, Grid, Typography, Button } from '@mui/material';
import { toast } from 'react-toastify';
import DropDownSingle from '../../../../../../Components/DropDownSingle';

const {
	SITE_STATUS: { CONCLUDED }
} = EXPB_DOC_CONSTANTS;

const {
	CUSTOM_TARGETING: { KEY }
} = AB_SPLIT_TEST_CONSTANTS;

const { NORMAL_SPLIT } = SPLIT_CONFIG_TYPES;

const { CUSTOM_TARGETING_KEYDATA, SEC_PV_TARGETING, DEFAULT_PV } = CONFIG_KEYDATA_CONSTANTS;

/**
 * @name AbSplitTestConcludeExpModal
 * @description This component is the modal to confirm the action of concluding the A/B Split experiment.
 * User can either proceed with the conclusion with "YES" or cancel the action with "NO".
 */
const AbSplitTestConcludeExpModal = ({ experimentData, index }) => {
	const { updateModalContent } = useContext(Modalv2Context);
	const commonStyleClasses = useCommonStyles();
	return (
		<Box
			className={commonStyleClasses.modalContentStyle}
			style={{ textAlign: 'center', padding: '4rem' }}
		>
			<Typography variant="h6" color="primary">
				Are you sure you want to Conclude Experiment?
			</Typography>
			<Box style={{ textAlign: 'center', gap: '2rem', marginTop: '4rem' }}>
				<Button
					variant="contained"
					onClick={() =>
						updateModalContent(<SelectWinnerModal experimentData={experimentData} index={index} />)
					}
				>
					Yes
				</Button>
				<Button
					onClick={() => {
						updateModalContent(null);
					}}
				>
					No
				</Button>
			</Box>
		</Box>
	);
};

/**
 * @name SelectWinnerModal
 * @description The Modal Component for Selecting the winner split of the A/B Split Test, thus concluding the experiment.
 */
const SelectWinnerModal = ({ experimentData, index }) => {
	const commonStyleClasses = useCommonStyles();
	const {
		units,
		updateUnits,
		expbDoc,
		updateExpbDoc,
		sectionLevelSplit,
		updateSectionLevelSplit,
		updateNonArchivedUnitsArray
	} = useContext(UnitsContext);
	const { updateModalContent } = useContext(Modalv2Context);
	const [winnerSectionRef, setWinnerSectionRef] = useState('');
	const { updateSelectedUnit } = useContext(UnitsContext);

	/**
	 * @name filterSectionRefs
	 * @description Sections list based on whether it's an A/B or Multi-Split test.
	 */
	const getExperimentsUnderConcludedExperiment = useMemo(() => {
		const controlVideoSectionId = experimentData.controlVideoSectionId;

		const experimentType = experimentData.multiSplit
			? MULTI_SPLIT_TEST_CONSTANTS.IDENTIFIER
			: AB_SPLIT_TEST_CONSTANTS.IDENTIFIER;

		const sectionRefKeys = Object.keys(units);

		const filteredSections = sectionRefKeys.filter(
			(sectionKey) =>
				units[sectionKey].videoSectionId === controlVideoSectionId &&
				units[sectionKey][experimentType]
		);
		const sectionData = filteredSections.map((sectionKey) => ({
			label: getSectionNameToDisplay(units[sectionKey]),
			value: sectionKey
		}));

		return sectionData;
	}, [units, experimentData.controlVideoSectionId, experimentData.multiSplit]);

	/**
	 * @name mergeSectionLevelSplitData
	 * @description Function to Merge the Section Level Split Data of Control and Experiment Split.
	 */
	const mergeSectionLevelSplitData = (winnerSectionRef) => {
		const controlVideoSectionId = experimentData.controlVideoSectionId;
		const sectionLevelSplitConfigClone = structuredClone(sectionLevelSplit);
		const isSectionLevelSplit = sectionLevelSplit.splitLevel === SPLIT_LEVELS.SECTION_LEVEL_SPLIT;

		if (!sectionLevelSplit || !sectionLevelSplit.splits[controlVideoSectionId]) {
			return;
		}

		if (experimentData?.multiSplit || isSectionLevelSplit) {
			delete sectionLevelSplitConfigClone.splits[controlVideoSectionId];
			/**
			 * Setting the enabled key to false in case SECTION_LEVEL_SPLIT is in action as it throws
			 * on updating site config due to empty splits object being present (since we're removing the experiment split
			 * if SECTION_LEVEL_SPLIT is there rather than merging them & converting it to NORMAL_SPLIT)
			 */
			if (isSectionLevelSplit && !sectionLevelSplitConfigClone.splits) {
				sectionLevelSplitConfigClone.enabled = false;
			}
		} else {
			// If SectionLevelSplit has control video section Id, it will merge the splitPercentage of control and experiment section.
			// ControlExperimentSplits Object having A/B Split sectionRefs as Key.
			const controlExpSectionSplitObj =
				sectionLevelSplit.splits[controlVideoSectionId].controlExperimentSplits;
			// Array of object containing the splitPercentages.
			const controlExpSectionSplitObjValues = Object.values(controlExpSectionSplitObj);
			const splitPercentagesArray = controlExpSectionSplitObjValues.map(
				(key) => key.splitPercentage
			);
			const totalAbSplitPercentage = splitPercentagesArray.reduce(
				(totalPercentage, currPercentage) => totalPercentage + currPercentage,
				0
			);

			const winnerSplitType = units[winnerSectionRef]?.abSplitTestData?.splitType || ' ';

			sectionLevelSplitConfigClone.splits[controlVideoSectionId] = {
				splitPercentage: totalAbSplitPercentage,
				splitDescription: `${experimentData.experimentName} (${winnerSplitType}) is the Winner from the Experiment.`
			};
			sectionLevelSplitConfigClone.splits[controlVideoSectionId]['type'] = NORMAL_SPLIT;
		}

		updateSectionLevelSplit(sectionLevelSplitConfigClone);
	};

	const concludeAbTest = () => {
		if (!winnerSectionRef) {
			return toast.error('Please select a winner split to conclude the test.', {
				position: toast.POSITION.BOTTOM_RIGHT
			});
		}

		if (!experimentData.contextUrl || !experimentData.comment) {
			return toast.error(
				'Please make sure to fill in the Context URL and Comment fields before concluding.',
				{
					position: toast.POSITION.BOTTOM_RIGHT
				}
			);
		}
		const customTargetingKvpKeyData = `${CUSTOM_TARGETING_KEYDATA}.targeting`;
		const customPageTargetingKvpKeyData = `${CUSTOM_TARGETING_KEYDATA}.pageviewTargeting`;
		const customPageTargetingSecPvKeyData = `${customPageTargetingKvpKeyData}.${SEC_PV_TARGETING}`;
		const controlVideoSectionId = experimentData.controlVideoSectionId;
		const clonedUnits = structuredClone(units);
		const clonedExpbDoc = structuredClone(expbDoc);

		if (experimentData.multiSplit) {
			const expRefs = Object.keys(
				sectionLevelSplit.splits[controlVideoSectionId].controlExperimentSplits
			);

			expRefs.forEach((sectionRef) => {
				if (sectionRef !== winnerSectionRef) {
					delete clonedUnits[sectionRef];
				}
			});

			delete clonedUnits[winnerSectionRef].multiSplitTestData;
			delete clonedUnits[winnerSectionRef][customTargetingKvpKeyData][KEY];

			// once abSplitTestData is deleted, update the sec_pv_targeting key to default_pv
			clonedUnits[winnerSectionRef][customPageTargetingSecPvKeyData] = DEFAULT_PV;
		} else {
			const loserSplitSectionRef =
				units[winnerSectionRef].abSplitTestData &&
				units[winnerSectionRef].abSplitTestData.counterpartSectionRef;
			// If loserSplitSectionRef is not found, it returns an error toast message.
			if (!loserSplitSectionRef) {
				return toast.error('Loser Split Section Ref not found.', {
					position: toast.POSITION.BOTTOM_RIGHT
				});
			}

			delete clonedUnits[winnerSectionRef][customTargetingKvpKeyData][KEY];
			delete clonedUnits[loserSplitSectionRef];
			delete clonedUnits[winnerSectionRef].abSplitTestData;

			// once abSplitTestData is deleted, update the sec_pv_targeting key to default_pv
			clonedUnits[winnerSectionRef][customPageTargetingSecPvKeyData] = DEFAULT_PV;
		}

		clonedExpbDoc.experiments[index].status = CONCLUDED;
		const updateClonedNonArchivedUnitsArray = [...Object.keys(clonedUnits)];
		updateExpbDoc(clonedExpbDoc);
		updateUnits(clonedUnits);
		updateSelectedUnit(winnerSectionRef);
		mergeSectionLevelSplitData(winnerSectionRef);
		updateNonArchivedUnitsArray(updateClonedNonArchivedUnitsArray);
		updateModalContent(null);
	};

	return (
		<Grid
			container
			spacing={2}
			className={commonStyleClasses.modalContentStyle}
			style={{
				textAlign: 'center',
				padding: '1rem'
			}}
			width="fit-content !important"
			height="fit-content !important"
		>
			<Grid item xs={12} style={{ marginBottom: '2rem' }}>
				<Typography variant="h5" color="primary" style={{ marginBottom: '1rem' }}>
					Choose Winner Split
				</Typography>
				<Typography>
					(Note: The winner split will come into effect and loser will be deleted, this process is
					irreversible.)
				</Typography>
			</Grid>
			<Grid
				item
				xs={12}
				className={commonStyleClasses.modalContentItem}
				style={{ marginBottom: '2rem' }}
			>
				<DropDownSingle
					isObjectEditor={false}
					directValue={winnerSectionRef}
					updateDirectValue={setWinnerSectionRef}
					list={getExperimentsUnderConcludedExperiment}
					formControlVariant={'outlined'}
				/>
			</Grid>
			<Grid item xs={12} style={{ marginBottom: '2rem' }}>
				<Button variant="contained" onClick={() => concludeAbTest()}>
					Conclude
				</Button>
			</Grid>
		</Grid>
	);
};

export default AbSplitTestConcludeExpModal;
